import { createContext, useContext, useState, useEffect } from 'react';

const ContextMenuContext = createContext();

export function ContextMenuHook({ children }) {
    const [openMenus, setOpenMenus] = useState({});
    const [menuHierarchy, setMenuHierarchy] = useState({});

    // Close all menus on any left click
    useEffect(() => {
        const handleClick = () => {
            if (Object.keys(openMenus).length > 0) {
                setOpenMenus({});
                setMenuHierarchy({});
            }
        };

        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [openMenus]);

    const openMenu = (menuId, position, parentMenuId = null) => {
        setOpenMenus(prev => {
            // If this menu has a parent, keep the parent and its ancestors open
            if (parentMenuId) {
                const newMenus = {};

                // Keep the immediate parent AND its ancestor chain
                const ancestorChain = new Set([parentMenuId]); // Start with parent itself
                let currentParent = menuHierarchy[parentMenuId];

                while (currentParent) {
                    ancestorChain.add(currentParent);
                    currentParent = menuHierarchy[currentParent];
                }

                // Keep only ancestors (including the immediate parent)
                Object.keys(prev).forEach(id => {
                    if (ancestorChain.has(id)) {
                        newMenus[id] = prev[id];
                    }
                });

                newMenus[menuId] = position;
                return newMenus;
            } else {
                // No parent means this is a root menu, close everything else
                return { [menuId]: position };
            }
        });

        // Update hierarchy AFTER setting menus
        if (parentMenuId) {
            setMenuHierarchy(prev => ({ ...prev, [menuId]: parentMenuId }));
        } else {
            setMenuHierarchy({});
        }
    };

    const closeMenu = (menuId) => {
        setOpenMenus(prev => {
            const newMenus = { ...prev };

            // Close this menu and all its children
            const menusToClose = new Set([menuId]);

            // Find all children recursively
            Object.entries(menuHierarchy).forEach(([childId, parentId]) => {
                if (menusToClose.has(parentId)) {
                    menusToClose.add(childId);
                }
            });

            // Remove all menus to close
            menusToClose.forEach(id => delete newMenus[id]);

            return newMenus;
        });

        // Clean up hierarchy
        setMenuHierarchy(prev => {
            const newHierarchy = { ...prev };
            Object.keys(newHierarchy).forEach(childId => {
                if (childId === menuId || newHierarchy[childId] === menuId) {
                    delete newHierarchy[childId];
                }
            });
            return newHierarchy;
        });
    };

    const closeAllMenus = () => {
        setOpenMenus({});
        setMenuHierarchy({});
    };

    const isMenuOpen = (menuId) => menuId in openMenus;

    const getMenuPosition = (menuId) => openMenus[menuId] || null;

    return (
        <ContextMenuContext.Provider value={{
            openMenu,
            closeMenu,
            closeAllMenus,
            isMenuOpen,
            getMenuPosition
        }}>
            {children}
        </ContextMenuContext.Provider>
    );
}

export function useContextMenu() {
    const context = useContext(ContextMenuContext);
    if (!context) {
        throw new Error('useContextMenu must be used within ContextMenuProvider');
    }
    return context;
}